import { useEffect, useMemo, useState } from 'react';
import { HallMapViewer, type MapTable } from '@lmac/ui';
import { PayPalPanel } from './PayPalButtons';
import {
  formatCents,
  formatCode,
  clubDate,
  generateCodeBody,
  looksLikeEmail,
  type PaymentMethod,
  type PublicMapChunk,
  type SwapMeetEvent,
  type Vendor,
} from '@lmac/shared';

/**
 * What a vendor actually does: pick tables, say who they are, choose how to
 * pay, confirm.
 *
 * The process this replaces is on the club's own page today — email the club,
 * wait to be told which tables you have, then click a PayPal button. Last
 * year's hall sold out that way, which is the strongest argument for holds:
 * when the room fills, two people WILL choose table 58 within the same minute,
 * and somebody has to lose that race cleanly rather than by email timestamp.
 *
 * Until Cloud Functions exist, `onSubmit` is absent and the flow runs as a
 * preview: everything is clickable, nothing is written.
 */

export type Step = 'tables' | 'details' | 'payment' | 'review' | 'pay' | 'done';

const BLANK_VENDOR: Vendor = {
  name: '',
  businessName: null,
  email: '',
  phone: '',
  address: null,
  amaNumber: null,
  sellingDescription: null,
};

export interface ReserveProps {
  event: SwapMeetEvent;
  map: PublicMapChunk;
  /** Absent while reservations are not live; the flow then runs as a preview. */
  onSubmit?: (
    vendor: Vendor,
    method: PaymentMethod,
    tableIds: string[],
  ) => Promise<{ reservationId: string; code: string }>;
}

export function Reserve({ event, map, onSubmit }: ReserveProps) {
  const [step, setStep] = useState<Step>('tables');
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [vendor, setVendor] = useState<Vendor>(BLANK_VENDOR);
  const [method, setMethod] = useState<PaymentMethod>('paypal');
  const [errors, setErrors] = useState<string[]>([]);
  /**
   * When the tables were claimed. Everything from here on runs against a
   * clock, because the tables are off the map for everyone else while this
   * vendor fills in a form — and last year's hall sold out, so that matters.
   */
  const [heldSince, setHeldSince] = useState<number | null>(null);
  const [expired, setExpired] = useState(false);
  /**
   * In the live flow the server mints this inside the claim transaction, which
   * is the only place uniqueness can be enforced. Here it is generated locally
   * so the preview shows the real shape of what a vendor receives.
   */
  const [code, setCode] = useState(() => formatCode(generateCodeBody()));
  /** Set once the server has a reservation, so PayPal knows what to charge for. */
  const [liveId, setLiveId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  /**
   * Explicit agreement to the forfeit terms, required only for paying at the
   * meet. An unpaid table is the club's risk, not the vendor's, so the terms
   * have to be read and accepted rather than buried in small print.
   */
  const [acceptedAtDoor, setAcceptedAtDoor] = useState(false);

  const tables: MapTable[] = useMemo(
    () => map.tables.map((t) => ({
      id: t.id, label: t.label, x: t.x, y: t.y, w: t.w, h: t.h,
      status: t.status, nearPower: t.nearPower,
    })),
    [map.tables],
  );

  const byId = useMemo(
    () => new Map(map.tables.map((t) => [t.id, t])),
    [map.tables],
  );

  const available = tables.filter((t) => t.status === 'available').length;
  const chosen = [...selected]
    .map((id) => byId.get(id))
    .filter((t): t is NonNullable<typeof t> => Boolean(t))
    .sort((a, b) => Number(a.label) - Number(b.label));

  // Day-of pricing applies to paying at the meet; see the flyer, which is
  // explicit that $15 means paid in advance.
  const rate = method === 'at_door' ? event.dayOfPricePerTable : event.pricePerTable;
  const deadline = clubDate(event.reservationsCloseAt);
  const total = chosen.length * rate;
  const atCap = chosen.length >= event.maxTablesPerVendor;

  const holdMs = event.policy.holdMinutes * 60_000;

  /** Leaving the map for the rest of the form is where the hold clock starts. */
  const beginReservation = () => {
    setHeldSince(Date.now());
    setStep('details');
  };

  const releaseEverything = () => {
    // In the live flow this also calls releaseHold so the tables go straight
    // back on the map rather than waiting for the sweep to notice.
    setSelected(new Set());
    setHeldSince(null);
    setErrors([]);
    setStep('tables');
  };

  const toggle = (id: string) => {
    setExpired(false);
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else if (next.size < event.maxTablesPerVendor) next.add(id);
      return next;
    });
  };

  const validateDetails = (): string[] => {
    const problems: string[] = [];
    if (!vendor.name.trim()) problems.push('Please give us your name.');
    if (!looksLikeEmail(vendor.email)) {
      problems.push('We need a working email address to send your confirmation to.');
    }
    if (vendor.phone.replace(/\D/g, '').length < 10) {
      problems.push('Please give a phone number we can reach you on the day.');
    }
    return problems;
  };

  return (
    <div className="rz">
      {!onSubmit && (
        <p className="rz__preview">
          <b>Preview.</b> Online reservations are not open yet — click through
          as much as you like, nothing is saved and no table is held.
        </p>
      )}

      {expired && (
        <p className="rz__expired">
          <b>Your hold ran out.</b> The tables went back on the map so somebody
          else could take them. Nothing was charged — pick again when ready.
        </p>
      )}

      {heldSince !== null && step !== 'done' && chosen.length > 0 && (
        <HoldClock
          startedAt={heldSince}
          durationMs={holdMs}
          onExpire={() => {
            setExpired(true);
            releaseEverything();
          }}
          count={chosen.length}
        />
      )}

      <Steps current={step} />

      {step === 'tables' && (
        <section>
          <div className="rz__head">
            <h2>Choose your tables</h2>
            <p className="rz__sub">
              {available} of {map.tables.length} still free. Tap a table to take
              it. Up to {event.maxTablesPerVendor} per vendor.
            </p>
          </div>

          {/* Two columns once there is room: the hall is 148ft wide and wants
              every pixel, while the cart is a narrow list that does not. */}
          <div className="rz__split">
            <div className="rz__split-map">
              <HallMapViewer
                caption={`${event.venueName} — tap to choose`}
                tables={tables}
                width={map.map.width}
                height={map.map.height}
                content={map.content}
                background={map.background}
                outlets={map.outlets}
                checkInTables={map.checkInTables}
                selected={selected}
                onToggle={toggle}
                fullscreenActions={(exit) => (
                  <>
                    <span className="rz__dock-sum">
                      {chosen.length === 0 ? (
                        <b>No tables chosen yet</b>
                      ) : (
                        <>
                          <b>
                            Table {chosen.map((t) => t.label).join(', ')}
                          </b>
                          <span>
                            {chosen.length} × {formatCents(rate)} ={' '}
                            {formatCents(total)}
                          </span>
                        </>
                      )}
                    </span>
                    <span className="rz__dock-btns">
                      <button className="rz__btn" onClick={exit}>
                        Close map
                      </button>
                      <button
                        className="rz__btn rz__btn--go"
                        disabled={chosen.length === 0}
                        onClick={() => {
                          // Leave fullscreen first: the rest of the form is
                          // outside this element and would be invisible.
                          exit();
                          beginReservation();
                        }}
                      >
                        {chosen.length === 0
                          ? 'Pick a table first'
                          : `Reserve ${chosen.length} table${chosen.length > 1 ? 's' : ''}`}
                      </button>
                    </span>
                  </>
                )}
              />
              <div className="mapkey">
                <span className="mapkey__title">Key</span>
                <span><i className="sw sw--free" />Available</span>
                <span><i className="sw sw--pick" />Yours</span>
                <span><i className="sw sw--pending" />Pending</span>
                <span><i className="sw sw--gone" />Reserved</span>
                <span><i className="sw sw--off" />Club table</span>
                <span><i className="sw sw--power" />Power nearby</span>
              </div>
            </div>

            <aside className="rz__split-side">
              <Cart chosen={chosen} rate={rate} total={total} onRemove={toggle} />

              {atCap && (
                <p className="rz__note">
                  That&rsquo;s the maximum of {event.maxTablesPerVendor}{' '}
                  tables. To take more, email{' '}
                  <a href={`mailto:${event.contactEmail}`}>{event.contactEmail}</a>.
                </p>
              )}

              <div className="rz__actions">
                <button
                  className="rz__btn rz__btn--go"
                  disabled={chosen.length === 0}
                  onClick={beginReservation}
                >
                  {chosen.length === 0
                    ? 'Pick at least one table'
                    : `Reserve ${chosen.length} table${chosen.length > 1 ? 's' : ''}`}
                </button>
              </div>
            </aside>
          </div>
        </section>
      )}

      {step === 'details' && (
        <section className="rz__measured">
          <div className="rz__head">
            <h2>Your details</h2>
            <p className="rz__sub">
              So we can send your confirmation and find you on the day.
            </p>
          </div>

          <div className="rz__form">
            <Field id="v-name" label="Your name" required
              value={vendor.name}
              onChange={(v) => setVendor({ ...vendor, name: v })} />
            <Field id="v-email" label="Email" type="email" required
              hint="Your confirmation and table numbers go here."
              value={vendor.email}
              onChange={(v) => setVendor({ ...vendor, email: v })} />
            <Field id="v-phone" label="Phone" type="tel" required
              value={vendor.phone}
              onChange={(v) => setVendor({ ...vendor, phone: v })} />
            <Field id="v-address" label="Mailing address" optional
              hint="Needed if you post a check; kept on file either way."
              value={vendor.address ?? ''}
              onChange={(v) => setVendor({ ...vendor, address: v || null })} />
            <Field id="v-biz" label="Business or club name" optional
              value={vendor.businessName ?? ''}
              onChange={(v) => setVendor({ ...vendor, businessName: v || null })} />
            <Field id="v-ama" label="AMA number" optional
              hint="If you have one."
              value={vendor.amaNumber ?? ''}
              onChange={(v) => setVendor({ ...vendor, amaNumber: v || null })} />
            <Field id="v-sell" label="What are you bringing?" optional
              hint="Helps the club advertise the meet. Shown to nobody else."
              value={vendor.sellingDescription ?? ''}
              onChange={(v) => setVendor({ ...vendor, sellingDescription: v || null })} />
          </div>

          {errors.length > 0 && (
            <ul className="rz__errors">
              {errors.map((e) => <li key={e}>{e}</li>)}
            </ul>
          )}

          <div className="rz__actions">
            <CancelButton onCancel={releaseEverything} count={chosen.length} />
            <button className="rz__btn" onClick={() => setStep('tables')}>Back</button>
            <button
              className="rz__btn rz__btn--go"
              onClick={() => {
                const problems = validateDetails();
                setErrors(problems);
                if (problems.length === 0) setStep('payment');
              }}
            >
              Continue
            </button>
          </div>
        </section>
      )}

      {step === 'payment' && (
        <section className="rz__measured">
          <div className="rz__head">
            <h2>How would you like to pay?</h2>
            <p className="rz__sub">
              Paying now is the only way to lock your tables in straight away.
            </p>
          </div>

          <div className="rz__pay">
            <PayChoice
              id="pay-paypal" checked={method === 'paypal'}
              onSelect={() => { setMethod('paypal'); setAcceptedAtDoor(false); }}
              title="Pay now by card or PayPal"
              price={formatCents(event.pricePerTable)}
              outcome="confirmed"
              body="Your tables are yours the moment the payment goes through. You get an emailed receipt with your table numbers."
            />
            {event.policy.allowCheckByMail && (
              <PayChoice
                id="pay-check" checked={method === 'check'}
                onSelect={() => { setMethod('check'); setAcceptedAtDoor(false); }}
                title="Mail a check"
                price={formatCents(event.pricePerTable)}
                outcome="held"
                body={`Your tables are held until the check arrives. It has to reach us by ${deadline}, or they go back on the map.`}
              >
                <span className="rz__mail">
                  <span>
                    Make it out to <b>{event.policy.checkPayableTo}</b>
                  </span>
                  <span className="rz__addr">{event.policy.checkMailingAddress}</span>
                  <span className="rz__mail-note">
                    The treasurer&rsquo;s address, not the hall. Arriving by{' '}
                    {deadline} is what counts &mdash; not when it clears.
                  </span>
                </span>
              </PayChoice>
            )}
            {event.policy.allowPayAtDoor && (
              <PayChoice
                id="pay-door" checked={method === 'at_door'}
                onSelect={() => { setMethod('at_door'); setAcceptedAtDoor(false); }}
                title="Pay at the meet"
                price={formatCents(event.dayOfPricePerTable)}
                outcome="approval"
                body={`A club officer has to approve this one. It costs the day-of rate of ${formatCents(event.dayOfPricePerTable)}, because a table promised and not paid for is a table nobody else could take. We will email you either way.`}
              />
            )}
          </div>

          {method === 'at_door' && (
            <div className="rz__terms">
              <h3>Please read before choosing this</h3>
              <p>
                Paying at the meet is the <b>least certain</b> way to reserve.
                Your tables are not confirmed when you submit this &mdash; a
                club officer has to approve them, and they can be released if
                that does not happen in time.
              </p>
              <p>
                Nothing happens automatically with this option. If you have not
                heard back within a few days, email{' '}
                <a href={`mailto:${event.contactEmail}?subject=Swap%20meet%20table%20reservation`}>
                  {event.contactEmail}
                </a>{' '}
                and quote your confirmation number.
              </p>
              <label className="rz__terms-check" htmlFor="accept-at-door">
                <input
                  id="accept-at-door"
                  type="checkbox"
                  checked={acceptedAtDoor}
                  onChange={(e) => setAcceptedAtDoor(e.target.checked)}
                />
                <span>
                  I understand that my reservation must be confirmed by a club
                  officer, and that if it is not confirmed by <b>{deadline}</b>{' '}
                  my table{chosen.length > 1 ? 's' : ''} will be{' '}
                  <b>forfeited and returned to the open pool</b> for anyone else
                  to book. I understand the rate is{' '}
                  {formatCents(event.dayOfPricePerTable)} a table rather than{' '}
                  {formatCents(event.pricePerTable)}, and that paying online now
                  is the only way to secure my tables immediately.
                </span>
              </label>
            </div>
          )}

          <div className="rz__actions">
            <CancelButton onCancel={releaseEverything} count={chosen.length} />
            <button className="rz__btn" onClick={() => setStep('details')}>Back</button>
            <button
              className="rz__btn rz__btn--go"
              disabled={method === 'at_door' && !acceptedAtDoor}
              onClick={() => setStep('review')}
            >
              {method === 'at_door' && !acceptedAtDoor
                ? 'Please tick the box above'
                : 'Review'}
            </button>
          </div>
        </section>
      )}

      {step === 'review' && (
        <section className="rz__measured">
          <div className="rz__head">
            <h2>Check this over</h2>
          </div>

          <dl className="rz__review">
            <div><dt>Confirmation</dt><dd className="rz__code">{code}</dd></div>
            <div><dt>Tables</dt><dd>{chosen.map((t) => t.label).join(', ')}</dd></div>
            <div><dt>Name</dt><dd>{vendor.name}</dd></div>
            <div><dt>Email</dt><dd>{vendor.email}</dd></div>
            <div><dt>Phone</dt><dd>{vendor.phone}</dd></div>
            {vendor.address && <div><dt>Address</dt><dd>{vendor.address}</dd></div>}
            {vendor.businessName && <div><dt>Business</dt><dd>{vendor.businessName}</dd></div>}
            {vendor.amaNumber && <div><dt>AMA</dt><dd>{vendor.amaNumber}</dd></div>}
            <div><dt>Paying by</dt><dd>{methodLabel(method)}</dd></div>
            <div className="rz__review-total">
              <dt>{chosen.length} × {formatCents(rate)}</dt>
              <dd>{formatCents(total)}</dd>
            </div>
          </dl>

          {method === 'check' && (
            <div className="rz__mailbox">
              <h3>Where to post the check</h3>
              <p>
                Payable to <b>{event.policy.checkPayableTo}</b>
              </p>
              <p className="rz__addr">{event.policy.checkMailingAddress}</p>
              <p className="rz__mail-note">
                Must arrive by {deadline}. Write <b>{code}</b> on the memo line
                — that is how the treasurer matches your check to these tables.
              </p>
            </div>
          )}

          {method === 'at_door' && (
            <p className="rz__terms rz__terms--recap">
              <b>Not yet confirmed.</b> A club officer must approve this
              reservation. If it is not confirmed by {deadline}, your tables are
              forfeited and returned to the open pool. Chase it at{' '}
              <a href={`mailto:${event.contactEmail}`}>{event.contactEmail}</a>.
            </p>
          )}

          <p className="rz__policy">{event.policy.refundPolicyText}</p>

          {errors.length > 0 && (
            <ul className="rz__errors">
              {errors.map((e) => <li key={e}>{e}</li>)}
            </ul>
          )}

          <div className="rz__actions">
            <CancelButton onCancel={releaseEverything} count={chosen.length} />
            <button className="rz__btn" onClick={() => setStep('payment')}>Back</button>
            <button
              className="rz__btn rz__btn--go"
              disabled={submitting}
              onClick={() => {
                if (!onSubmit) {
                  // Preview: skip straight to the confirmation.
                  setStep(method === 'paypal' ? 'pay' : 'done');
                  return;
                }
                setSubmitting(true);
                setErrors([]);
                void onSubmit(vendor, method, chosen.map((t) => t.id))
                  .then((r) => {
                    setLiveId(r.reservationId);
                    setCode(r.code);
                    setStep(method === 'paypal' ? 'pay' : 'done');
                  })
                  .catch((e: unknown) => setErrors([describeFailure(e)]))
                  .finally(() => setSubmitting(false));
              }}
            >
              {submitting
                ? 'Just a moment…'
                : method === 'paypal'
                  ? `Pay ${formatCents(total)}`
                  : 'Send my reservation'}
            </button>
          </div>
        </section>
      )}

      {step === 'pay' && (
        <section className="rz__measured">
          <div className="rz__head">
            <h2>Pay {formatCents(total)}</h2>
            <p className="rz__sub">
              Table {chosen.map((t) => t.label).join(', ')} &middot;{' '}
              confirmation {code}
            </p>
          </div>

          {liveId ? (
            <PayPalPanel
              reservationId={liveId}
              amountLabel={formatCents(total)}
              onPaid={() => setStep('done')}
              onFailed={(m) => setErrors([m])}
            />
          ) : (
            <p className="rz__preview">
              <b>Preview.</b> The PayPal buttons appear here in the real thing.
              Nothing is charged and no table is held.
            </p>
          )}

          <p className="rz__policy">
            Your tables are held while you pay. {event.policy.refundPolicyText}
          </p>

          <div className="rz__actions">
            <CancelButton onCancel={releaseEverything} count={chosen.length} />
            <button className="rz__btn" onClick={() => setStep('review')}>Back</button>
          </div>

          {errors.length > 0 && (
            <ul className="rz__errors">{errors.map((e) => <li key={e}>{e}</li>)}</ul>
          )}
        </section>
      )}

      {step === 'done' && (
        <section className="rz__done rz__measured">
          {/*
            Live and preview need genuinely different words here. Somebody who
            has just paid needs telling plainly that it is done — conditional
            phrasing on a confirmation screen reads as "something went wrong",
            which is the last thing to imply after taking money.
          */}
          {onSubmit ? (
            <>
              <h2>
                {method === 'paypal'
                  ? 'Paid. Your tables are reserved.'
                  : 'Your tables are reserved.'}
              </h2>

              <p className="rz__code-big">
                <span>Your confirmation number</span>
                <b>{code}</b>
                <span className="rz__code-why">
                  {method === 'check'
                    ? `Write this on the check memo line and post it to arrive by ${deadline}.`
                    : 'Quote this in any email to the club about your reservation.'}
                </span>
              </p>

              <p>
                <b>Table {chosen.map((t) => t.label).join(', ')}</b> &middot;{' '}
                {event.dateLabel}
                <br />
                {event.venueName}, {event.venueAddress}
                <br />
                <span className="rz__hint">{event.setupLabel}</span>
              </p>
              <p>A confirmation is on its way to {vendor.email}.</p>

              {method === 'paypal' && (
                <p>Nothing further to do — bring your confirmation number on the day.</p>
              )}
              {method === 'check' && (
                <div className="rz__mailbox">
                  <h3>Where to post the check &mdash; not the venue</h3>
                  <p>
                    Payable to <b>{event.policy.checkPayableTo}</b>
                  </p>
                  <p className="rz__addr">{event.policy.checkMailingAddress}</p>
                  <p className="rz__mail-note">
                    Must arrive by {deadline}. Write {code} on the memo line.
                    This is the treasurer&rsquo;s address, not the hall.
                  </p>
                </div>
              )}
              {method === 'at_door' && (
                <div className="rz__terms">
                  <h3>Not confirmed yet</h3>
                  <p>
                    A club officer has to approve this. We will email you either
                    way. If you have not heard by <b>{deadline}</b>, your tables
                    are released &mdash; so chase it at{' '}
                    <a href={`mailto:${event.contactEmail}?subject=Reservation%20${code}`}>
                      {event.contactEmail}
                    </a>{' '}
                    quoting <b>{code}</b>.
                  </p>
                  <p>
                    Bring {formatCents(total)} on the day, cash or card.
                  </p>
                </div>
              )}
            </>
          ) : (
            <>
              <h2>That is how it would end.</h2>
              <p className="rz__code-big">
                <span>Your confirmation number would be</span>
                <b>{code}</b>
                <span className="rz__code-why">
                  Nothing was saved and no table is held — this is a preview.
                </span>
              </p>
              <p>
                In the real thing you would now have{' '}
                <b>table {chosen.map((t) => t.label).join(', ')}</b> and an email
                confirming it.
              </p>
            </>
          )}

          <button
            className="rz__btn"
            onClick={() => {
              setStep('tables');
              setSelected(new Set());
              // The previous reservation is finished; none of its state should
              // follow the vendor into the next one.
              setHeldSince(null);
              setLiveId(null);
              setCode(formatCode(generateCodeBody()));
              setErrors([]);
              setExpired(false);
            }}
          >
            {onSubmit ? 'Reserve more tables' : 'Start again'}
          </button>
        </section>
      )}
    </div>
  );
}

function methodLabel(m: PaymentMethod): string {
  return m === 'paypal' ? 'Card or PayPal, now'
    : m === 'check' ? 'Check in the mail'
    : 'Cash at the meet';
}

function Steps({ current }: { current: Step }) {
  const order: Step[] = ['tables', 'details', 'payment', 'review', 'pay'];
  const names = ['Tables', 'Details', 'Payment', 'Review', 'Pay'];
  const i = order.indexOf(current);
  return (
    <ol className="rz__steps">
      {names.map((n, k) => (
        <li key={n} className={k === i ? 'is-now' : k < i ? 'is-done' : ''}>
          <span>{k + 1}</span>{n}
        </li>
      ))}
    </ol>
  );
}

function Cart({
  chosen, rate, total, onRemove,
}: {
  chosen: { id: string; label: string; nearPower: boolean }[];
  rate: number;
  total: number;
  onRemove: (id: string) => void;
}) {
  if (chosen.length === 0) return null;
  return (
    <div className="rz__cart">
      <ul>
        {chosen.map((t) => (
          <li key={t.id}>
            <span>
              Table {t.label}
              {t.nearPower && <em> · power nearby</em>}
            </span>
            <button onClick={() => onRemove(t.id)} aria-label={`Remove table ${t.label}`}>
              Remove
            </button>
          </li>
        ))}
      </ul>
      <p className="rz__cart-total">
        <span>{chosen.length} × {formatCents(rate)}</span>
        <b>{formatCents(total)}</b>
      </p>
    </div>
  );
}

function PayChoice({
  id, checked, onSelect, title, price, body, outcome, children,
}: {
  id: string;
  checked: boolean;
  onSelect: () => void;
  title: string;
  price: string;
  body: string;
  outcome: 'confirmed' | 'held' | 'approval';
  /** Detail revealed only once this option is chosen, to keep the list scannable. */
  children?: React.ReactNode;
}) {
  const badge =
    outcome === 'confirmed' ? 'Tables confirmed straight away'
    : outcome === 'held' ? 'Tables held, not yet confirmed'
    : 'Needs a club officer to approve';
  return (
    <label className={`rz__choice${checked ? ' is-on' : ''}`} htmlFor={id}>
      <input id={id} type="radio" name="pay" checked={checked} onChange={onSelect} />
      <span className="rz__choice-body">
        <span className="rz__choice-top">
          <b>{title}</b>
          <span className="rz__choice-price">{price} a table</span>
        </span>
        <span className={`rz__badge rz__badge--${outcome}`}>{badge}</span>
        <span className="rz__choice-text">{body}</span>
        {checked && children}
      </span>
    </label>
  );
}

function Field({
  id, label, value, onChange, type = 'text', required, optional, hint,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  required?: boolean;
  optional?: boolean;
  hint?: string;
}) {
  return (
    <div className="rz__field">
      <label htmlFor={id}>
        {label}
        {optional && <em> (optional)</em>}
      </label>
      <input
        id={id}
        type={type}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete={
          type === 'email' ? 'email' : type === 'tel' ? 'tel'
          : id === 'v-name' ? 'name' : 'off'
        }
      />
      {hint && <p className="rz__hint">{hint}</p>}
    </div>
  );
}

/**
 * The countdown on a hold.
 *
 * Worth showing rather than hiding: the tables are off the map for everybody
 * else while this vendor types, so the clock is the honest explanation for why
 * they cannot take all afternoon. It turns urgent in the last two minutes,
 * which is the point at which a warning can still change what someone does.
 */
function HoldClock({
  startedAt, durationMs, onExpire, count,
}: {
  startedAt: number;
  durationMs: number;
  onExpire: () => void;
  count: number;
}) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  const left = Math.max(0, startedAt + durationMs - now);

  useEffect(() => {
    if (left === 0) onExpire();
  }, [left, onExpire]);

  if (left === 0) return null;

  const mins = Math.floor(left / 60_000);
  const secs = Math.floor((left % 60_000) / 1000);
  const urgent = left <= 120_000;

  return (
    <p className={`rz__clock${urgent ? ' is-urgent' : ''}`} role="status">
      <span>
        {count} table{count > 1 ? 's' : ''} held for you
      </span>
      <b>
        {mins}:{String(secs).padStart(2, '0')}
      </b>
      <span className="rz__clock-why">
        {urgent
          ? 'About to be released'
          : 'Nobody else can take them until this runs out'}
      </span>
    </p>
  );
}

/** Explicit abandon, which puts every held table straight back on the map. */
function CancelButton({ onCancel, count }: { onCancel: () => void; count: number }) {
  return (
    <button
      className="rz__btn rz__btn--cancel"
      onClick={() => {
        const ok = window.confirm(
          `Give up ${count} table${count > 1 ? 's' : ''}? ` +
            'They go straight back on the map for somebody else.',
        );
        if (ok) onCancel();
      }}
    >
      Cancel
    </button>
  );
}

/**
 * Turn a server error into something a vendor can act on.
 *
 * The raw messages are already written for people, but the losing side of a
 * race needs telling what to DO — the tables it names are gone, and the fix is
 * to go back and choose again, not to try the same thing harder.
 */
function describeFailure(e: unknown): string {
  const raw = e instanceof Error ? e.message : String(e);
  if (/just taken by somebody else/i.test(raw)) {
    return `${raw} Please go back and choose different tables.`;
  }
  if (/hold ran out|deadline/i.test(raw)) {
    return 'Your hold ran out while you were filling this in. Please choose your tables again.';
  }
  if (/unauthenticated|session expired/i.test(raw)) {
    return 'Your session expired. Please reload the page and start again.';
  }
  return raw;
}
